if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();

        this.input = this.querySelector('.quantity__input');
        this.currentVariant = this.querySelector('.product-variant-id');
        this.submitButton = this.querySelector('[type="submit"]');

        // Throttle / cancel quantity rules updates to avoid request storms (429)
        this._quantityRulesTimer = null;
        this._quantityRulesAbortController = null;
        this._lastQuantityRulesFetchAt = 0;
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        if (!this.input) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        // IMPORTANT:
        // Default Refresh subscribes to cartUpdate and then fetches the product section HTML on EVERY cart update.
        // If the user changes cart quantities quickly (or you have extra cart updates from other scripts), this can
        // generate lots of GET /products/... requests and trigger Shopify 429 rate limiting.
        //
        // We still subscribe, but we first try to update cart quantity from the cartUpdate payload (no fetch).
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
          if (!event || !event.data || event.data.sectionId !== sectionId) return;

          this.updateQuantityRules(event.data.sectionId, event.data.html);
          this.setQuantityBoundries();
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();

        if (this._quantityRulesTimer) {
          clearTimeout(this._quantityRulesTimer);
          this._quantityRulesTimer = null;
        }

        if (this._quantityRulesAbortController) {
          try {
            this._quantityRulesAbortController.abort();
          } catch (e) {}
          this._quantityRulesAbortController = null;
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
          min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
          max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
          step: this.input.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;

        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.input.min = min;

        if (max) {
          this.input.max = max;
        } else {
          this.input.removeAttribute('max');
        }

        this.input.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      /**
       * cartUpdate handler
       * - Prefer using event.cartData (already contains the latest quantities) to update data-cart-quantity.
       * - Only fetch as a fallback (throttled + debounced) if event.cartData isn't provided.
       */
      onCartUpdate(event) {
        if (!this.input || !this.currentVariant || !this.currentVariant.value) return;

        const currentVariantId = Number(this.currentVariant.value);
        const cartData = event && event.cartData;

        // Fast path: no network request
        if (cartData && Array.isArray(cartData.items)) {
          const match = cartData.items.find((item) => Number(item.variant_id) === currentVariantId);
          const qtyInCart = match ? Number(match.quantity || 0) : 0;

          this.input.dataset.cartQuantity = String(qtyInCart);
          this.setQuantityBoundries();
          return;
        }

        // If the event tells us WHICH variant changed and it's not ours, skip
        if (event && event.variantId && String(event.variantId) !== String(currentVariantId)) return;

        // Fallback
        this.scheduleFetchQuantityRules();
      }

      scheduleFetchQuantityRules() {
        if (!this.currentVariant || !this.currentVariant.value) return;

        // Debounce + rate limit
        const MIN_INTERVAL_MS = 1200;
        const elapsed = Date.now() - this._lastQuantityRulesFetchAt;
        const delay = elapsed >= MIN_INTERVAL_MS ? 250 : Math.max(250, MIN_INTERVAL_MS - elapsed);

        if (this._quantityRulesTimer) clearTimeout(this._quantityRulesTimer);
        this._quantityRulesTimer = setTimeout(() => {
          this.fetchQuantityRules();
        }, delay);
      }

      fetchQuantityRules() {
        if (!this.currentVariant || !this.currentVariant.value) return;

        // Throttle marker
        this._lastQuantityRulesFetchAt = Date.now();

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Abort previous request
        if (this._quantityRulesAbortController) {
          try {
            this._quantityRulesAbortController.abort();
          } catch (e) {}
        }
        this._quantityRulesAbortController = new AbortController();

        const url = `${this.dataset.url}?variant=${encodeURIComponent(
          this.currentVariant.value
        )}&section_id=${encodeURIComponent(this.dataset.section)}`;

        fetch(url, {
          signal: this._quantityRulesAbortController.signal,
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'text/html',
          },
        })
          .then((response) => {
            // If Shopify rate limits (429) or any error, DON'T parse and DON'T throw later.
            if (!response.ok) throw new Error(`Quantity rules fetch failed (${response.status})`);
            return response.text();
          })
          .then((responseText) => {
            if (!this.isConnected) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.setQuantityBoundries();
          })
          .catch((e) => {
            if (e && e.name === 'AbortError') return;
            console.error(e);
          })
          .finally(() => {
            if (spinner) spinner.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityForm || !html) return;

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) {
          // If Shopify returns a non-section HTML page (e.g. 429 page), this will be null.
          // Never throw here — errors inside pubsub subscribers can break cart updates.
          return;
        }

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }
    }
  );
}